import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useSelector } from 'react-redux';

import { Loader, Button } from '@c';
import { useActions } from '@h';
import { courseActions } from '@r/courses/courseSlice';
import { toClassNames } from '@helps/';

import * as Blocks from './BlockTypes';
import { Input } from './Input';
import { TestContext } from '../utils';

import styles from './style.module.scss';

const contentBlockTypes = [
    'text',
    'textWithTypes',
    'question',
    'questionWithTypes',
    'image',
    'video',
];

const Course = ({ type = '', scheme = [], lessonMeta = {}, onReturnToEditor = () => {} }) => {
    const { stopAudio, playAudio } = useContext(TestContext);

    const { isLoading, currentNode, currentTheme } = useSelector(
        ({ course }) => course
    );
    const { currentNodeUpdate, replaySchemeStart, currentNodeStart } =
        useActions(courseActions);

    const [value, setValue] = useState('');
    const [isFinished, setIsFinished] = useState(false);
    const [playerStartedAt, setPlayerStartedAt] = useState(Date.now());

    const totalBlocks = useMemo(
        () =>
            (scheme || []).filter((el) => contentBlockTypes.includes(el.type))
                .length,
        [scheme]
    );

    const toNext = () => {
        stopAudio();
        currentNodeUpdate({
            courseId: currentTheme,
            type: type,
            answer: value,
        });
    };

    const toPrev = () => {
        stopAudio();
        currentNodeUpdate({
            courseId: currentTheme,
            type,
            prev: true,
        });
    };

    const resetBlock = () => {
        stopAudio();
        currentNodeStart({ courseId: currentTheme, type });
    };

    const reset = () => {
        stopAudio();
        replaySchemeStart({ courseId: currentTheme, type });
    };

    const handleFinish = () => setIsFinished(true);

    const handleReturnToEditor = () => onReturnToEditor();

    const handleRestart = () => {
        setIsFinished(false);
        setPlayerStartedAt(Date.now());
        reset();
    };

    const handleBackFromFinish = () => setIsFinished(false);

    const formatElapsed = () => {
        const diff = Date.now() - playerStartedAt;
        const minutes = Math.round(diff / 60000);
        return minutes < 1 ? 'менее минуты' : `${minutes} минут`;
    };

    const finishSubtitle = [
        lessonMeta.courseName,
        [lessonMeta.subject, lessonMeta.klass].filter(Boolean).join(', '),
    ]
        .filter(Boolean)
        .join(' — ');

    useEffect(() => {
        setValue('');
        if (currentNode && currentNode.data?.speech) {
            playAudio(
                `${process.env.REACT_APP_API_URL}${currentNode.data.speech}`
            );
        } else {
            console.log('No audio for node');
        }
    }, [currentNode]);

    useEffect(() => {
        if (!isFinished) return;

        const onKey = (event) => {
            if (event.key === 'Escape') {
                onReturnToEditor();
            }
        };

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isFinished, onReturnToEditor]);

    return (
        <div className={styles.main}>
            <div className={styles.theme}>
                {isFinished ? lessonMeta.courseName : currentTheme.theme}
            </div>
            <div className={styles.container}>
                {isFinished ? (
                    <div className={styles.finish_screen}>
                        <h2 className={styles.finish_title}>Урок завершён!</h2>
                        {finishSubtitle && (
                            <p className={styles.finish_subtitle}>
                                {finishSubtitle}
                            </p>
                        )}
                        <div className={styles.finish_metrics}>
                            <p>
                                Пройдено блоков: {totalBlocks} из {totalBlocks}
                            </p>
                            <p>Время в уроке: {formatElapsed()}</p>
                        </div>
                        <p className={styles.finish_hint}>
                            Это предпросмотр — статистика не сохраняется
                        </p>
                    </div>
                ) : !currentNode || isLoading ? (
                    <Loader color="rgb(0, 110, 255)" />
                ) : (
                    React.createElement(Blocks[currentNode.type], {
                        data: currentNode.data,
                        onAnswerChange: (val) => setValue(val),
                    })
                )}
            </div>
            <div className={styles.buttons}>
                {isFinished ? (
                    <Button
                        className={styles.button}
                        onClick={handleReturnToEditor}
                    >
                        Вернуться в редактор
                    </Button>
                ) : currentNode?.type === 'question' ? (
                    currentNode.data.type === 'multi' ? (
                        <Button
                            className={styles.button}
                            onClick={() => !isLoading && toNext()}
                            title={'Дальше (ctrl + enter)'}
                            disabled={isLoading}
                        >
                            Подтвердить
                        </Button>
                    ) : (
                        <Input
                            withYesHelps={currentNode.data.type === 'yesNo'}
                            value={value}
                            setValue={setValue}
                            onSend={() => !isLoading && toNext()}
                        />
                    )
                ) : currentNode?.type === 'finish' ? (
                    <Button
                        className={styles.button}
                        onClick={handleFinish}
                        disabled={isLoading}
                    >
                        Завершить
                    </Button>
                ) : (
                    <Button
                        className={styles.button}
                        onClick={() => !isLoading && toNext()}
                        title={'Дальше (ctrl + enter)'}
                        disabled={isLoading}
                    >
                        Дальше
                    </Button>
                )}
                <div className={styles.update}>
                    <Button
                        className={toClassNames(styles.button)}
                        onClick={handleRestart}
                        disabled={isLoading}
                    >
                        Начать с начала
                    </Button>
                    <Button
                        className={toClassNames(styles.button)}
                        onClick={resetBlock}
                        disabled={isLoading || isFinished}
                    >
                        Обновить блок
                    </Button>
                    <Button
                        className={toClassNames(styles.button)}
                        onClick={isFinished ? handleBackFromFinish : toPrev}
                        disabled={isLoading}
                    >
                        Назад
                    </Button>
                </div>
            </div>
        </div>
    );
};

export { Course };
